import { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Grid,
  List,
  Row,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router';
import type { AdminProduct } from '@titularis/api-client';
import { api } from '../../api';
import { condicaoDoPreco, emReais, FORMA_LABEL } from '../../dominio';
import { FormularioDeProduto } from './FormularioDeProduto';
import { SITUACAO_COR, SITUACAO_LABEL, situacaoDoProduto, somarCatalogo } from './situacao';

/**
 * Produtos: o que se vende, por quanto, e o que a compra libera.
 *
 * **Existe porque o catálogo só tinha uma entrada: um script.** As tabelas
 * `products` e `prices` nasceram no PR-11 com a decisão de que o catálogo é
 * nosso e não do gateway - o Asaas não tem objeto de produto nem de preço -, e
 * até aqui mudar um preço era editar o `seed-billing.ts` e rodá-lo contra o
 * banco de produção. Numa semana de lançamento, isso é a diferença entre uma
 * decisão comercial e um deploy.
 *
 * A tela responde três perguntas, e a ordem delas é a ordem da página: **o que
 * está à venda hoje**, **quanto entrou** e **o que cada produto libera**.
 *
 * O rodapé de cada linha diz "sem preço ativo" quando é o caso, e essa etiqueta
 * não é decoração: o servidor só mostra na vitrine produto ativo **com preço
 * ativo**, então um produto ligado e sem preço é invisível para o aluno - e era
 * indistinguível de um à venda em qualquer lugar do backoffice.
 */
export function ProdutosPage() {
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const navigate = useNavigate();
  const [criando, setCriando] = useState(false);

  const produtos = useQuery({
    queryKey: ['produtos'],
    queryFn: () => api.listAdminProducts(),
  });

  const lista = produtos.data ?? [];
  const total = somarCatalogo(lista);

  /** Os preços ativos de um produto, em uma linha por forma de pagamento. */
  const precosAtivos = (produto: AdminProduct) =>
    produto.precos.filter((preco) => preco.active);

  const resumoDePrecos = (produto: AdminProduct) => {
    const ativos = precosAtivos(produto);
    if (ativos.length === 0) {
      return <Typography.Text type="secondary">sem preço</Typography.Text>;
    }

    return (
      <Flex vertical gap={2}>
        {ativos.map((preco) => (
          <Typography.Text key={preco.id} style={{ whiteSpace: 'nowrap' }}>
            {FORMA_LABEL[preco.kind] ?? preco.kind} {emReais(preco.amountCents)}{' '}
            <Typography.Text type="secondary">
              {condicaoDoPreco(preco.amountCents, preco.maxInstallments)}
            </Typography.Text>
          </Typography.Text>
        ))}
      </Flex>
    );
  };

  const etiquetaDeSituacao = (produto: AdminProduct) => {
    const situacao = situacaoDoProduto(produto);
    return (
      <Tag color={SITUACAO_COR[situacao]} style={{ marginInlineEnd: 0 }}>
        {SITUACAO_LABEL[situacao]}
      </Tag>
    );
  };

  const colunas = [
    {
      title: 'Produto',
      key: 'produto',
      render: (_: unknown, linha: AdminProduct) => (
        <Flex vertical gap={0}>
          <Typography.Link onClick={() => void navigate(`/produtos/${linha.id}`)}>
            {linha.name}
          </Typography.Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <code>{linha.key}</code>
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Concede',
      key: 'grants',
      render: (_: unknown, linha: AdminProduct) => (
        <Flex gap={4} wrap>
          {linha.grants.map((chave) => (
            <Tag key={chave} style={{ marginInlineEnd: 0 }}>
              {linha.rotulos[chave] ?? chave}
            </Tag>
          ))}
        </Flex>
      ),
    },
    { title: 'Preços', key: 'precos', render: (_: unknown, l: AdminProduct) => resumoDePrecos(l) },
    {
      title: 'Vendas',
      key: 'vendas',
      align: 'right' as const,
      render: (_: unknown, linha: AdminProduct) => (
        <Flex vertical gap={0} align="flex-end">
          <Typography.Text>{linha.vendas.pagos}</Typography.Text>
          {linha.vendas.abertos > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {linha.vendas.abertos} em aberto
            </Typography.Text>
          )}
        </Flex>
      ),
    },
    {
      title: 'Receita',
      key: 'receita',
      align: 'right' as const,
      render: (_: unknown, linha: AdminProduct) => emReais(linha.vendas.receitaCents),
    },
    {
      title: 'Situação',
      key: 'situacao',
      render: (_: unknown, linha: AdminProduct) => etiquetaDeSituacao(linha),
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap gap={12}>
        <div>
          {/* Nível 3 come duas linhas em 390px, como nas demais telas do admin. */}
          <Typography.Title level={compacto ? 4 : 3} style={{ marginBottom: 4 }}>
            Produtos
          </Typography.Title>
          <Typography.Text type="secondary">
            O que se vende, por quanto e o que a compra libera. Preço muda sem deploy.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCriando(true)}>
          Novo produto
        </Button>
      </Flex>

      {produtos.error && (
        <Alert type="error" showIcon message={(produtos.error as Error).message} />
      )}

      {/*
        Quatro números, e nenhum deles é "total de produtos": o catálogo tem um
        punhado de linhas e quem administra as vê logo abaixo. O que não se
        enxerga na tabela é quanto entrou e quanto está pendurado num checkout
        que ninguém terminou.
      */}
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Receita confirmada" value={emReais(total.receitaCents)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Vendas pagas" value={total.pagos} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Checkouts em aberto" value={total.abertos} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="À venda" value={total.aVenda} suffix={`/ ${lista.length}`} />
          </Card>
        </Col>
      </Row>

      {/*
        No celular a tabela vira lista, como nas demais telas do admin: seis
        colunas em 390px não encolhem, quebram - e a primeira a sair da rolagem
        horizontal seria justamente o preço.
      */}
      {compacto ? (
        <List
          loading={produtos.isLoading}
          dataSource={lista}
          locale={{ emptyText: <Empty description="Nenhum produto cadastrado." /> }}
          renderItem={(produto) => (
            <List.Item style={{ paddingInline: 0 }}>
              <Card
                size="small"
                style={{ width: '100%' }}
                onClick={() => void navigate(`/produtos/${produto.id}`)}
              >
                <Flex vertical gap={8} style={{ minWidth: 0 }}>
                  <Flex justify="space-between" align="flex-start" gap={8}>
                    <Flex vertical gap={0} style={{ minWidth: 0 }}>
                      <Typography.Text strong>{produto.name}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        <code>{produto.key}</code>
                      </Typography.Text>
                    </Flex>
                    {etiquetaDeSituacao(produto)}
                  </Flex>

                  {resumoDePrecos(produto)}

                  <Flex gap={4} wrap>
                    {produto.grants.map((chave) => (
                      <Tag key={chave} style={{ marginInlineEnd: 0 }}>
                        {produto.rotulos[chave] ?? chave}
                      </Tag>
                    ))}
                  </Flex>

                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {produto.vendas.pagos} venda(s) · {emReais(produto.vendas.receitaCents)}
                  </Typography.Text>
                </Flex>
              </Card>
            </List.Item>
          )}
        />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            dataSource={lista}
            columns={colunas}
            loading={produtos.isLoading}
            pagination={false}
            locale={{ emptyText: <Empty description="Nenhum produto cadastrado." /> }}
            /*
             * Largura em número, e não `x: true`: com `true` o Antd mede o
             * conteúdo e uma coluna larga empurra a última para fora da tela
             * sem criar a rolagem que a traria de volta.
             */
            scroll={{ x: 900 }}
          />
        </Card>
      )}

      <FormularioDeProduto
        aberto={criando}
        aoFechar={() => setCriando(false)}
        aoCriar={(produto) => {
          setCriando(false);
          void navigate(`/produtos/${produto.id}`);
        }}
      />
    </Flex>
  );
}
